import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { useBootAnimation } from '../../hooks/useBootAnimation';
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Boot: the word "anna" dissolves into her orb, then she says she is ready for your instruction.
 * Tap, click or any key skips.
 */
export function AnnaBoot({
  onDone
}: {
  onDone: () => void;
}) {
  const {
    accentHex
  } = useSpecSettings();
  const reduced = useMotionPreference();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    phase,
    skip
  } = useBootAnimation(canvasRef, accentHex, reduced);
  useEffect(() => {
    if (phase === 'done') onDone();
  }, [phase, onDone]);
  useEffect(() => {
    const onKey = () => skip();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);
  return <motion.div role="status" aria-live="polite" aria-label="Anna start op" className="fixed inset-0 z-[100] cursor-pointer bg-night-950" onClick={skip} initial={{
    opacity: 1
  }} exit={{
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: EASE
    }
  }} data-id="element-523" data-mp="3sv2d4mmwr8na">
      {reduced ? <div className="grid h-full place-items-center" data-id="element-524" data-mp="1ir6tvxag7cm">
          <p className="font-display text-[96px] font-extralight text-ink" data-id="element-525" data-mp="39wzrmhtkjtu8">anna</p>
        </div> : <canvas ref={canvasRef} aria-hidden className="absolute inset-0" data-id="element-526" data-mp="34yoiyaf0fpdl" />}

      <div className="pointer-events-none absolute inset-x-0 top-[66%] flex flex-col items-center px-6 text-center" data-id="element-527" data-mp="3ixxwt3fqu7ee">
        <AnimatePresence data-id="element-528" data-mp="3evt5wcgojovg">
          {phase === 'ready' && <motion.div key="ready" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} data-id="element-529" data-mp="3eyp84czk8zpp">
              <p className="font-display text-[34px] font-light text-ink lg:text-[44px]" data-id="element-530" data-mp="3mziw069hah4j">Anna is er.</p>
              <p className="mt-2 text-[18px] text-ink-soft lg:text-[22px]" data-id="element-531" data-mp="3rym4k0oo35h9">Klaar voor je instructie.</p>
            </motion.div>}
        </AnimatePresence>
      </div>
      <span className="sr-only" data-id="element-532" data-mp="4vhbphqby5z1">{phase === 'ready' ? 'Anna is klaar voor je instructie' : 'Anna start op'}</span>
      <p className="pointer-events-none absolute inset-x-0 bottom-10 text-center text-[13px] text-ink-muted" data-id="element-533" data-mp="34avufxqig9vp">Tik om over te slaan</p>
    </motion.div>;
}